import { useForm } from "react-hook-form";
import InputWithLabel from "../InputWithLabel";
import Tooltip from "../Tooltip";
import {
  company_address,
  company_name,
  company_reg_number,
  email,
} from "../../constants/tooltipContents";
import {
  useAddPartnerCompanyMutation,
  useUpdatePartnerCompanyMutation,
} from "../../store/apiSlices/partnerCompany";
import { SpinnerIcon } from "../Icons";
import { toast } from "react-toastify";
import { PartnerCompanyDetails } from "../../types/PartnerCompanyDetails";

type FormType = Omit<
  PartnerCompanyDetails,
  "partner_company_id" | "created_at" | "uid"
>;

type Props = {
  action: "add" | "update";
  toggleModal: () => void;
  selectedCompany?: PartnerCompanyDetails;
};

export default function PartnerCompanyForm({
  action,
  toggleModal,
  selectedCompany,
}: Props) {
  const [addPartnerCompany] = useAddPartnerCompanyMutation();
  const [updatePartnerCompany] = useUpdatePartnerCompanyMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormType>({
    defaultValues: action === "update" ? selectedCompany : undefined,
  });

  const onSubmit = async (values: FormType) => {
    // Trim all string fields
    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    ) as FormType;

    try {
      let data;

      if (action === "add") {
        // Call add mutation
        data = await addPartnerCompany(trimmedValues).unwrap();
      } else {
        // Call update mutation
        if (!selectedCompany?.partner_company_id)
          throw new Error("No company selected for update");
        data = await updatePartnerCompany({
          ...trimmedValues,
          partner_company_id: selectedCompany.partner_company_id,
        }).unwrap();
      }

      // Handle response
      if (data.status === "success") {
        reset();
        toggleModal();
        toast.success(data.message);
      } else {
        toast.error(data.message || "Something went wrong, please try again!");
      }
    } catch (err: any) {
      // console.error(err);
      toast.error(err?.data?.message || "Network error, please try again!");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Company Name */}
        <InputWithLabel
          label="Company Name *"
          placeholder="Enter here"
          type="text"
          element={<Tooltip content={company_name} />}
          error={errors.company_name?.message}
          {...register("company_name", {
            required: "Company name is required",
            minLength: {
              value: 2,
              message: "Company name must be at least 2 characters",
            },
            validate: (value) =>
              value.trim().length > 0 || "Company name cannot be empty",
          })}
        />

        {/* Company Address */}
        <InputWithLabel
          label="Company Address *"
          placeholder="Enter here"
          type="text"
          element={<Tooltip content={company_address} />}
          error={errors.company_address?.message}
          {...register("company_address", {
            required: "Company address is required",
            minLength: {
              value: 5,
              message: "Company address must be at least 5 characters",
            },
            validate: (value) =>
              value.trim().length > 0 || "Company address cannot be empty",
          })}
        />

        {/* Registration Number */}
        <InputWithLabel
          label="Company Registration Number *"
          placeholder="Enter here"
          element={<Tooltip content={company_reg_number} />}
          type="text"
          error={errors.company_registration_number?.message}
          {...register("company_registration_number", {
            required: "Registration number is required",
            minLength: {
              value: 3,
              message: "Registration number must be at least 3 characters",
            },
            validate: (value) =>
              value.trim().length > 0 || "Registration number cannot be empty",
          })}
        />

        {/* Contact Person */}
        <InputWithLabel
          label="Contact Person Name *"
          placeholder="Enter here"
          type="text"
          error={errors.contact_person_name?.message}
          {...register("contact_person_name", {
            required: "Contact person name is required",
            minLength: {
              value: 2,
              message: "Contact person name must be at least 2 characters",
            },
            validate: (value) =>
              value.trim().length > 0 || "Contact person name cannot be empty",
          })}
        />

        {/* Email */}
        <InputWithLabel
          label="Email Address *"
          placeholder="Enter here"
          element={<Tooltip content={email} />}
          type="email"
          error={errors.email?.message}
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Enter a valid email address",
            },
            validate: (value) =>
              value.trim().length > 0 || "Email cannot be empty",
          })}
        />

        {/* Phone */}
        <InputWithLabel
          label="Phone Number *"
          placeholder="Enter here"
          type="text"
          error={errors.phone_number?.message}
          {...register("phone_number", {
            required: "Phone number is required",
            minLength: {
              value: 7,
              message: "Phone number must be at least 7 digits",
            },
            validate: (value) =>
              value.trim().length > 0 || "Phone number cannot be empty",
          })}
        />
      </div>

      {/* Buttons */}
      <div className="pt-4 flex justify-between gap-2">
        <button
          type="button"
          onClick={toggleModal}
          className="w-[87px] h-11 text-custom-blue-medium font-medium border border-custom-blue-medium rounded-lg"
        >
          Cancel
        </button>

        <button
          disabled={isSubmitting}
          className="px-4 py-2 bg-custom-blue-medium text-white font-medium rounded-lg disabled:bg-custom-blue-medium/60"
        >
          {isSubmitting ? (
            <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-custom-blue-medium" />
          ) : (
            "Save"
          )}
        </button>
      </div>
    </form>
  );
}
